import { CURRENCY } from 'App';
import {
  DEFAULT_MAX_STAKE,
  DEFAULT_MIN_STAKE,
  DEFAULT_MIN_STAKE_PER_BET,
  MAX_WIN_FOR_TICKET,
} from 'config/constants';
import { Dispatch, SetStateAction } from 'react';
import { SlipInputValueTypes } from 'types/enums';

export const validateInputs = (
  totalStake: number,
  stakePerBet: number | undefined,
  priorityField: SlipInputValueTypes,
  t: (_: string) => string
) => {
  if (priorityField === SlipInputValueTypes.TOTAL_STAKE) {
    validateTotalStake(totalStake, t);
    validateStakePerBet(stakePerBet, t);
  } else {
    validateStakePerBet(stakePerBet, t);
    validateTotalStake(totalStake, t);
  }
};

const validateTotalStake = (totalStake: number, t: (_: string) => string) => {
  const minStake = Number.parseFloat(
    process.env.REACT_APP_MIN_TOTAL_STAKE ?? DEFAULT_MIN_STAKE
  );

  const maxStake = Number.parseFloat(
    process.env.REACT_APP_MAX_STAKE ?? DEFAULT_MAX_STAKE
  );
  if (totalStake > maxStake) {
    throw new Error(
      t('max_stake_error_message')
        .replace('{value}', maxStake.toFixed(2))
        .replace('{currency}', CURRENCY)
    );
  } else if (totalStake < minStake) {
    throw new Error(
      t('min_stake_error_message')
        .replace('{value}', minStake.toFixed(2))
        .replace('{currency}', CURRENCY)
    );
  }
};

const validateStakePerBet = (
  stakePerBet: number | undefined,
  t: (_: string) => string
) => {
  const minStakePerBet = Number.parseFloat(
    process.env.REACT_APP_MIN_STAKE_PER_BET ?? DEFAULT_MIN_STAKE_PER_BET
  );

  if (!!stakePerBet && stakePerBet < minStakePerBet) {
    throw new Error(
      t('min_stake_error_message')
        .replace('{value}', minStakePerBet.toFixed(2))
        .replace('{currency}', CURRENCY)
    );
  }
};

/**
 * The largest stake whose potential win stays within the ticket maximum
 * (potential win = total stake x total odds), rounded down to the cent.
 */
export const maxStakeFor = (maxCoefficient: number): number =>
  maxCoefficient > 0
    ? Math.floor((MAX_WIN_FOR_TICKET / maxCoefficient) * 100) / 100
    : 0;

export const round2Decimals = (num: number): number => {
  return Math.round(num * 100) / 100;
};

export const handleSlipInputChange = (
  version: SlipInputValueTypes,
  value: number,
  { betNumber, maxCoefficient }: SlipInputValues,
  { setTotalStake, setStakePerBet, setMaxGain }: SlipInputStateActions,
  t: (_: string) => string
) => {
  if (version === SlipInputValueTypes.TOTAL_STAKE) {
    validateInputs(
      value,
      betNumber > 1 ? value / betNumber : undefined,
      SlipInputValueTypes.STAKE_PER_BET,
      t
    );
    setTotalStake(value);
    if (betNumber > 1) {
      setStakePerBet(value / betNumber);
    }
    setMaxGain(value * maxCoefficient);
  } else if (version === SlipInputValueTypes.STAKE_PER_BET) {
    validateInputs(
      value * betNumber,
      value,
      SlipInputValueTypes.STAKE_PER_BET,
      t
    );
    setStakePerBet(value);
    setTotalStake(value * betNumber);
    setMaxGain(value * betNumber * maxCoefficient);
  } else {
    validateInputs(
      value / maxCoefficient,
      betNumber > 1 ? value / maxCoefficient / betNumber : undefined,
      SlipInputValueTypes.TOTAL_STAKE,
      t
    );
    setTotalStake(value / maxCoefficient);
    if (betNumber > 1) {
      setStakePerBet(value / maxCoefficient / betNumber);
    }
  }
};

/**
 * Recomputes the slip's other amounts while the player types in one of them
 * (stake, stake per bet or potential win), so everything follows at once.
 * No validation here: that happens when the field is left
 * (handleSlipInputChange), so no warning pops up mid-typing.
 */
export const recomputeSlipValues = (
  version: SlipInputValueTypes,
  value: number,
  { betNumber, maxCoefficient }: SlipInputValues,
  { setTotalStake, setStakePerBet, setMaxGain }: SlipInputStateActions
) => {
  if (version === SlipInputValueTypes.TOTAL_STAKE) {
    setTotalStake(value);
    if (!Number.isFinite(value)) return;
    if (betNumber > 1) setStakePerBet(value / betNumber);
    setMaxGain(value * maxCoefficient);
  } else if (version === SlipInputValueTypes.STAKE_PER_BET) {
    setStakePerBet(value);
    if (!Number.isFinite(value)) return;
    setTotalStake(value * betNumber);
    setMaxGain(value * betNumber * maxCoefficient);
  } else {
    setMaxGain(value);
    if (!Number.isFinite(value) || maxCoefficient <= 0) return;
    setTotalStake(value / maxCoefficient);
    if (betNumber > 1) setStakePerBet(value / maxCoefficient / betNumber);
  }
};

export interface SlipInputValues {
  betNumber: number;
  maxCoefficient: number;
}

export interface SlipInputStateActions {
  setStakePerBet: Dispatch<SetStateAction<number>>;
  setTotalStake: Dispatch<SetStateAction<number>>;
  setMaxGain: Dispatch<SetStateAction<number>>;
}
